import { ReactNode, lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { AiAssistant } from "@/components/AiAssistant";
import { CookieConsent } from "@/components/CookieConsent";
import { LanguageSuggestion } from "@/components/LanguageSuggestion";
import { EventBanner } from "@/components/EventBanner";

// Caricato dopo il primo render (idle): non serve al primo paint
const ExitIntentPopup = lazy(() =>
  import("@/components/ExitIntentPopup").then((m) => ({ default: m.ExitIntentPopup })),
);
import { trackPageView, sendTimeOnPage } from "@/lib/analytics-tracker";

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [location] = useLocation();
  const prevLocation = useRef<string | null>(null);
  const [showExitPopup, setShowExitPopup] = useState(false);

  useEffect(() => {
    // Monta il popup solo dopo che il main thread è libero
    const hasIdle = typeof window.requestIdleCallback === "function";
    const idle = hasIdle
      ? window.requestIdleCallback(() => setShowExitPopup(true))
      : window.setTimeout(() => setShowExitPopup(true), 2000);
    return () => {
      if (hasIdle) window.cancelIdleCallback(idle as number);
      else window.clearTimeout(idle as number);
    };
  }, []);

  useEffect(() => {
    // Invia tempo sulla pagina precedente prima di tracciare la nuova
    if (prevLocation.current !== null && prevLocation.current !== location) {
      sendTimeOnPage();
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    prevLocation.current = location;
    trackPageView();
  }, [location]);

  return (
    <div className="flex min-h-[100dvh] w-full min-w-0 max-w-full flex-col overflow-x-hidden">
      <Navbar />
      <LanguageSuggestion />
      <main className="flex-1 pt-[72px] md:pt-[84px]">
        {children}
      </main>
      <Footer />
      <WhatsAppButton />
      <AiAssistant />
      <CookieConsent />
      <EventBanner />
      {showExitPopup && (
        <Suspense fallback={null}>
          <ExitIntentPopup />
        </Suspense>
      )}
    </div>
  );
}
